import api from "./client";
import axios from "axios";
import { getCurrentUser } from "./auth";


// Função para envio da movimentação
export async function enviarMovimentacao(payload: {
	tipo: "entrada" | "saida" | "transferencia";
	usuario_id: number;
	localizacao_origem_id: number;
	localizacao_destino_id: number;
	itens_movimentacao: {
		produto_id: number;
		quantidade: number;
		produto_estoque_id?: number;
	}[];
	data_hora_inicio?: string;
}) {
	try {
		const { data } = await api.post("/movimentacao", payload);
		return data;
	} catch (err: any) {
		if (axios.isAxiosError(err)) {
			if (err.response) {
				console.error("❌ Status do erro:", err.response.status);
				console.error(
					"❌ Dados da resposta:",
					JSON.stringify(err.response.data, null, 2),
				);

				// Se for erro 400 (Bad Request), pode ser estoque insuficiente
				if (err.response.status === 400) {
					const errorMessage = err.response.data.message || err.response.data;

					// Verifica se é erro de estoque insuficiente
					if (
						typeof errorMessage === "string" &&
						(errorMessage.includes("excede a disponível") ||
							errorMessage.includes("menor que a solicitada") ||
							errorMessage.includes("sem quantidade disponível") ||
							errorMessage.includes("Estoque insuficiente"))
					) {
						// Retorna um objeto especial para o frontend tratar
						const error = new Error(errorMessage);
						(error as any).type = "estoque_insuficiente";
						(error as any).originalError = err;
						throw error;
					}
				}

				// Para outros erros, mantém o comportamento original
				throw new Error(
					`Erro ${err.response.status}: ${
						err.response.data.message || JSON.stringify(err.response.data)
					}`,
				);
			} else if (err.request) {
				throw new Error("Nenhuma resposta recebida do servidor.");
			} else {
				throw new Error("Erro na configuração da requisição.");
			}
		} else {
			throw new Error("Erro inesperado ao enviar movimentação.");
		}
	}
}


// ---------- PERMISSÕES DE MOVIMENTAÇÃO ----------

// Interface para permissões de movimentação
export interface PermissoesMovimentacao {
	movimentacao_set: "entrada" | "saída";
	tipo_movimentacao_padrao: "entrada" | "saída" | "ambos";
}


// Função para obter permissões de movimentação do usuário
export async function getPermissoesMovimentacao(
	usuario_id: number,
): Promise<PermissoesMovimentacao> {
	try {
		const response = await api.get(`/usuario/${usuario_id}`);
		const usuario = response.data;

		return {
			movimentacao_set: usuario.movimentacao_set,
			tipo_movimentacao_padrao: usuario.tipo_movimentacao_padrao,
		};
	} catch (error) {
		console.error("Erro ao buscar permissões de movimentação:", error);
		throw error;
	}
}


// Função para atualizar permissões de movimentação do usuário
export async function atualizarPermissoesMovimentacao(
	usuario_id: number,
	permissoes: PermissoesMovimentacao,
): Promise<any> {
	try {
		const response = await api.patch(`/usuario/${usuario_id}`, {
			movimentacao_set: permissoes.movimentacao_set,
			tipo_movimentacao_padrao: permissoes.tipo_movimentacao_padrao,
		});
		return response.data;
	} catch (error) {
		console.error("Erro ao atualizar permissões de movimentação:", error);
		throw error;
	}
}


// Função para obter permissões de movimentação do usuário logado
export async function getCurrentUserPermissoesMovimentacao(): Promise<PermissoesMovimentacao> {
	try {
		const currentUser = await getCurrentUser();
		return await getPermissoesMovimentacao(currentUser.usuario_id);
	} catch (error) {
		console.error(
			"Erro ao buscar permissões de movimentação do usuário logado:",
			error,
		);
		throw error;
	}
}


// ✅ NOVA FUNÇÃO: Buscar histórico de movimentação com paginação otimizada
export async function buscarHistoricoMovimentacao(params?: {
	busca_unificada?: string;
	tipo_movimentacao?: string;
	data_inicio?: string;
	data_fim?: string;
	usuario_id?: number;
	armazem_id?: number;
	tipo_localizacao_id?: number;
	tarefa_id?: number;
	limit?: number;
	offset?: number;
}) {
	try {
		const queryParams: Record<string, any> = {};

		// Aplicar filtros
		if (params?.busca_unificada) {
			queryParams.busca_unificada = params.busca_unificada;
		}
		if (params?.tipo_movimentacao) {
			queryParams.tipo_movimentacao = params.tipo_movimentacao;
		}
		if (params?.data_inicio) {
			queryParams.data_inicio = params.data_inicio;
		}
		if (params?.data_fim) {
			queryParams.data_fim = params.data_fim;
		}
		if (params?.usuario_id) {
			queryParams.usuario_id = params.usuario_id;
		}
		if (params?.armazem_id) {
			queryParams.armazem_id = params.armazem_id;
		}
		if (params?.tipo_localizacao_id) {
			queryParams.tipo_localizacao_id = params.tipo_localizacao_id;
		}
		if (params?.tarefa_id) {
			queryParams.tarefa_id = params.tarefa_id;
		}

		// Paginação
		queryParams.limit = String(params?.limit ?? 50);
		queryParams.offset = String(params?.offset ?? 0);

		const response = await api.get("/movimentacao/historico/buscar", {
			params: queryParams,
		});

		return response.data;
	} catch (error: any) {
		console.error("Erro ao buscar histórico de movimentação:", error.message);
		if (error.response) {
			console.error("Response data:", error.response.data);
			console.error("Status:", error.response.status);
		}
		throw new Error("Falha ao carregar o histórico de movimentação.");
	}
}


// ✅ NOVA FUNÇÃO: Contar total de movimentações para paginação
export async function contarHistoricoMovimentacao(params?: {
	busca_unificada?: string;
	tipo_movimentacao?: string;
	data_inicio?: string;
	data_fim?: string;
	usuario_id?: number;
	armazem_id?: number;
	tipo_localizacao_id?: number;
	tarefa_id?: number;
}) {
	try {
		const queryParams: Record<string, any> = {};

		// Aplicar filtros (sem paginação)
		if (params?.busca_unificada) {
			queryParams.busca_unificada = params.busca_unificada;
		}
		if (params?.tipo_movimentacao) {
			queryParams.tipo_movimentacao = params.tipo_movimentacao;
		}
		if (params?.data_inicio) {
			queryParams.data_inicio = params.data_inicio;
		}
		if (params?.data_fim) {
			queryParams.data_fim = params.data_fim;
		}
		if (params?.usuario_id) {
			queryParams.usuario_id = params.usuario_id;
		}
		if (params?.armazem_id) {
			queryParams.armazem_id = params.armazem_id;
		}
		if (params?.tipo_localizacao_id) {
			queryParams.tipo_localizacao_id = params.tipo_localizacao_id;
		}
		if (params?.tarefa_id) {
			queryParams.tarefa_id = params.tarefa_id;
		}

		const response = await api.get("/movimentacao/historico/contar", {
			params: queryParams,
		});

		return response.data.total;
	} catch (error: any) {
		console.error("Erro ao contar histórico de movimentação:", error.message);
		if (error.response) {
			console.error("Response data:", error.response.data);
			console.error("Status:", error.response.status);
		}
		throw new Error("Falha ao contar o histórico de movimentação.");
	}
}


// Interface para conferência de movimentação
export interface ConferenciaMovimentacao {
	conferencia_movimentacao_id: number;
	observacoes?: string;
	data_hora_inicio?: string;
	data_hora_conclusao?: string;
	status: "pendente" | "em andamento" | "concluida" | "cancelada";
	gerou_movimentacao: boolean;
	usuario: {
		usuario_id: number;
		nome: string;
	};
	localizacao: {
		localizacao_id: number;
		codigo: string;
	};
	itens_conferencia?: ItemConferenciaMovimentacao[];
}


// Interface para item de conferência de movimentação
export interface ItemConferenciaMovimentacao {
	item_conferencia_movimentacao_id: number;
	quantidade_sistema: number;
	quantidade_fisica: number;
	diferenca: number;
	observacoes?: string;
	ajustado: boolean;
	produto_estoque: {
		produto_estoque_id: number;
		quantidade: number;
		produto: {
			produto_id: number;
			nome: string;
			ean: string;
		};
	};
}


// DTO para criar conferência de movimentação
export interface CreateConferenciaMovimentacaoDto {
	localizacao_id: number;
	usuario_id: number;
	observacoes?: string;
}


// DTO para adicionar item à conferência
export interface CreateItemConferenciaMovimentacaoDto {
	conferencia_movimentacao_id: number;
	produto_estoque_id: number;
	quantidade_fisica: number;
	observacoes?: string;
}


/**
 * Cria uma nova conferência de movimentação
 */
export async function criarConferenciaMovimentacao(
	dados: CreateConferenciaMovimentacaoDto,
): Promise<ConferenciaMovimentacao> {
	try {
		const res = await api.post("/movimentacao/conferencia", dados);
		return res.data;
	} catch (error: any) {
		console.error("Erro ao criar conferência de movimentação:", error.message);
		if (error.response) {
			console.error("Response data:", error.response.data);
			console.error("Status:", error.response.status);
		}
		throw new Error("Falha ao criar conferência de movimentação.");
	}
}


/**
 * Inicia uma conferência de movimentação
 */
export async function iniciarConferenciaMovimentacao(
	conferenciaId: number,
): Promise<ConferenciaMovimentacao> {
	try {
		const res = await api.patch(
			`/movimentacao/conferencia/${conferenciaId}/iniciar`,
		);
		return res.data;
	} catch (error: any) {
		console.error(
			"Erro ao iniciar conferência de movimentação:",
			error.message,
		);
		if (error.response) {
			console.error("Response data:", error.response.data);
			console.error("Status:", error.response.status);
		}
		throw new Error("Falha ao iniciar conferência de movimentação.");
	}
}


/**
 * Adiciona um item à conferência de movimentação
 */
export async function adicionarItemConferenciaMovimentacao(
	dados: CreateItemConferenciaMovimentacaoDto,
): Promise<ItemConferenciaMovimentacao> {
	try {
		const res = await api.post("/movimentacao/conferencia/item", dados);
		return res.data;
	} catch (error: any) {
		console.error("Erro ao adicionar item à conferência:", error.message);
		if (error.response) {
			console.error("Response data:", error.response.data);
			console.error("Status:", error.response.status);
		}
		throw new Error("Falha ao adicionar item à conferência.");
	}
}


/**
 * Conclui uma conferência de movimentação
 */
export async function concluirConferenciaMovimentacao(
	conferenciaId: number,
	conclusao?: string,
): Promise<{ conferencia: ConferenciaMovimentacao; movimentacoes: any[] }> {
	try {
		const res = await api.patch(
			`/movimentacao/conferencia/${conferenciaId}/concluir`,
			{
				conclusao: conclusao || "Conferência concluída sem observações.",
			},
		);
		return res.data;
	} catch (error: any) {
		console.error(
			"Erro ao concluir conferência de movimentação:",
			error.message,
		);
		if (error.response) {
			console.error("Response data:", error.response.data);
			console.error("Status:", error.response.status);
		}
		throw new Error("Falha ao concluir conferência de movimentação.");
	}
}


/**
 * Busca uma conferência específica
 */
export async function buscarConferenciaMovimentacao(
	conferenciaId: number,
): Promise<ConferenciaMovimentacao> {
	try {
		const res = await api.get(`/movimentacao/conferencia/${conferenciaId}`);
		return res.data;
	} catch (error: any) {
		console.error("Erro ao buscar conferência de movimentação:", error.message);
		if (error.response) {
			console.error("Response data:", error.response.data);
			console.error("Status:", error.response.status);
		}
		throw new Error("Falha ao buscar conferência de movimentação.");
	}
}


/**
 * Busca conferências por localização
 */
export async function buscarConferenciasPorLocalizacao(
	localizacaoId: number,
): Promise<ConferenciaMovimentacao[]> {
	try {
		const res = await api.get(
			`/movimentacao/conferencia/localizacao/${localizacaoId}`,
		);
		return res.data || [];
	} catch (error: any) {
		console.error(
			"Erro ao buscar conferências por localização:",
			error.message,
		);
		if (error.response) {
			console.error("Response data:", error.response.data);
			console.error("Status:", error.response.status);
		}
		return []; // Retorna array vazio em caso de erro
	}
}


/**
 * Cancela uma conferência de movimentação
 */
export async function cancelarConferenciaMovimentacao(
	conferenciaId: number,
): Promise<ConferenciaMovimentacao> {
	try {
		const res = await api.patch(
			`/movimentacao/conferencia/${conferenciaId}/cancelar`,
		);
		return res.data;
	} catch (error: any) {
		console.error(
			"Erro ao cancelar conferência de movimentação:",
			error.message,
		);
		if (error.response) {
			console.error("Response data:", error.response.data);
			console.error("Status:", error.response.status);
		}
		throw new Error("Falha ao cancelar conferência de movimentação.");
	}
}

